import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";

const TermsOfService = () => {
  useEffect(() => {
    document.title = "Terms of Service | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Review the terms and conditions for using the Hadassah Homes website. Learn about our policies on property listings, consultations, and more.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/terms-of-service";
  }, []);

  return (
    <SiteLayout>
      <PageHero eyebrow="Legal" title="Terms of Service" subtitle="Please Review Our Terms Before Using This Website" compact />

      <ContentSection>
        <div className="mx-auto max-w-prose space-y-6 text-lg md:text-xl leading-relaxed text-muted-foreground">
          <p>Welcome to Hadassah Homes. By accessing or using our website, you agree to be bound by the following terms and conditions. Please read them carefully.</p>
          <p>If you do not agree with any part of these terms, please do not use our website.</p>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader title="Use of Website" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">This website is provided for informational purposes related to real estate services offered by Hadassah Homes. You agree to use this website only for lawful purposes and in a manner that does not infringe upon the rights of others.</p>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader title="No Guarantee of Results" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">While we strive to provide accurate and helpful information, Hadassah Homes does not guarantee specific outcomes related to buying, selling, or investing in real estate. All decisions should be made with the guidance of qualified professionals.</p>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader title="Consultations and Advice" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Information provided during consultations or through our website is for general guidance only and should not be considered legal, financial, or investment advice. We recommend consulting with licensed professionals for specific decisions.</p>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader title="Property Listings" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Property listings and related information on our website are provided for informational purposes. We make every effort to ensure accuracy, but we do not guarantee that all listing details are current or error-free.</p>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader title="Third-Party Services" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Our website may reference or link to third-party services, tools, or websites. Hadassah Homes is not responsible for the content, accuracy, or practices of third-party providers.</p>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader title="Limitation of Liability" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Hadassah Homes shall not be held liable for any direct, indirect, incidental, or consequential damages arising from the use of this website or reliance on any information provided.</p>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader title="Intellectual Property" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">All content on this website—including text, images, logos, and design elements—is the property of Hadassah Homes and is protected by applicable intellectual property laws. You may not reproduce, distribute, or use any content without prior written permission.</p>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader title="Privacy" align="left" />
        <div className="mx-auto max-w-prose space-y-4">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Your use of this website is also governed by our Privacy Policy. Please review it to understand how we collect and use your information.</p>
          <Link to="/privacy-policy" className="inline-block font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors">
            View our Privacy Policy →
          </Link>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader title="Changes to These Terms" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We reserve the right to update or modify these Terms of Service at any time. Changes will be posted on this page with an updated effective date. Continued use of the website constitutes acceptance of any changes.</p>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader title="Governing Law" align="left" />
        <div className="mx-auto max-w-prose">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">These terms shall be governed by and construed in accordance with the laws of the Commonwealth of Massachusetts, without regard to its conflict of law principles.</p>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader title="Contact Us" align="left" />
        <div className="mx-auto max-w-prose space-y-4">
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">If you have any questions about these Terms of Service, please contact us:</p>
          <div className="space-y-2 text-foreground text-base md:text-lg">
            <p>Email: <a href="mailto:Esther@hadassahhomesinc.com" className="text-accent hover:text-accent/80 transition-colors">Esther@hadassahhomesinc.com</a></p>
            <p>Phone: <a href="tel:+17816323936" className="text-accent hover:text-accent/80 transition-colors">(781) 632-3936</a></p>
          </div>
        </div>
      </ContentSection>
    </SiteLayout>
  );
};

export default TermsOfService;
